import { memo, useState } from 'react';
import { Box, Group, Skeleton, Stack, Table, Text } from '@mantine/core';
import type { UseFormReturnType } from '@mantine/form';

import type { FormularDFormData } from '../../../types/reports.js';
import { formatTonnage } from '../../../utils/formatting.js';
import { ValidatedNumberInput } from '../../form/validatedFields.js';
import { DECIMAL_INPUT_PROPS } from '../../form/numberFieldPresets.js';
import {
  MATRIX_LABEL_TH,
  MATRIX_STICKY_LABEL_TD,
  MATRIX_STICKY_LABEL_TH,
  MATRIX_TABLE_STYLES,
  MATRIX_TOTAL_TH,
  MATRIX_VALUE_TH_COMPACT,
} from '../../form/matrixPresets.js';
import matrixClasses from '../../form/matrixTable.module.css';
import { SectionToggleRow } from '../../ui/SectionToggleRow.js';
import { PreviousValueForField } from '../../form/PreviousValueLine.js';
import { TabErrorDot } from '../ReportTabs.js';
import { tabsWithErrors } from '../tabErrors.js';
import type { CalcStore, ValuesVersionStore } from '../formStores.js';
import { CEM_GROUP_FIELD_MAP, CEMENT_GROUPS, STRENGTH_CLASSES, STRENGTH_LABELS, cemGroupErrorSig } from './catalog.js';
import { FormularDTotal } from './DerivedTotal.js';
import { calculateGroupTotal, calculateTypeTotal } from './totals.js';

/**
 * Abschnitt I der Zementartenstatistik (Formular D) -- sechs Akkordeon-Karten im Drehofen-Muster
 * (D-02), darin je eine Matrix-Tabelle mit den neun Festigkeitsklassen einmal als Kopfzeile statt
 * als wiederholtes Label ueber jedem einzelnen Feld (D-01). Ersetzt die lokale `CementGroup`-
 * Komponente aus `FormularDForm.tsx` (Plan 23-03 Task 1) -- der Umbau selbst folgt in Task 2.
 */

interface FormularDSectionIProps {
  form: UseFormReturnType<FormularDFormData>;
  store: ValuesVersionStore;
  calcStore: CalcStore;
  disabled?: boolean;
  loading?: boolean;
}

interface CementGroupCardProps {
  group: (typeof CEMENT_GROUPS)[number];
  form: UseFormReturnType<FormularDFormData>;
  store: ValuesVersionStore;
  calcStore: CalcStore;
  errorSig: string;
  formKey: string;
  hasError: boolean;
  disabled?: boolean;
  loading?: boolean;
}

/**
 * `memo()`-Comparator (D-03): faengt die RECONCILE-Last der 261 Felder ab, die sonst bei jedem
 * vom Elternteil ausgeloesten Render entsteht (jeder Tastendruck -> `setCalcKey` ->
 * `ReportEditPage` -> ... -> alle sechs Karten). Faengt NICHT die MOUNT-Last beim Tab-Wechsel
 * oder Aufklappen ab -- dafuer waere das zurueckgestellte Lazy-Rendering (D-02, bewusst nicht
 * Teil dieser Phase).
 *
 * Genau sechs Eintraege, keiner mehr:
 * - `errorSig` ist der EINZIGE Kanal, ueber den ein Backend-Fehler eine memoisierte Karte noch
 *   erreicht: `ValidatedNumberInput` liest seinen Fehler ueber `form.getInputProps(field).error`
 *   bei jedem eigenen Render frisch, ohne Abo. Die Signatur MUSS deshalb vollstaendig sein --
 *   fehlt ein Feldpfad in `CEM_GROUP_FIELD_MAP`, bleibt ein Fehler auf genau diesem Feld
 *   unsichtbar (T-19-27/T-19-28).
 * - `hasError` traegt den roten Punkt (D-07/D-14) -- bewusst NICHT aus `errorSig`
 *   zurueckgerechnet, weil eine Fehlermeldung mit einem `|` das brechen wuerde.
 * - `disabled` steuert die Bedienbarkeit aller 261 Felder (Sicherheitsrelevanz).
 * - `loading` schaltet zwischen Skeleton und Feld um.
 * - `group.id` ist die Identitaet der Karte.
 * - `formKey` schliesst Pitfall 1: `form.setValues()` bumpt in Mantine 9.2 den GLOBALEN
 *   `formKey`, nur darueber uebernehmen die uncontrolled Inputs nach dem stillen Nachladen
 *   (`fetchReport({silent:true})`) frische `defaultValue`s -- unabhaengig vom zufaelligen
 *   `disabled`-Flip des Speicherablaufs.
 *
 * Ausdruecklich NICHT im Comparator: `form` (neue Referenz pro Render, Methoden bleiben
 * ref-stabil -- ein Eintrag hebelte jedes Memo aus), `store`/`calcStore` (`useRef`-stabil, der
 * Vergleich waere immer `true`, also toter Code), `expanded` (lokaler `useState` IN der Karte,
 * kein Prop -- lokaler State loest ohnehin immer ein Re-Render aus) und der Vorwerte-Weg (D-06:
 * `usePreviousValue` abonniert feldgenau unter der Memo-Grenze, ein Eintrag waere wirkungslos).
 */
const CementGroupCard = memo(function CementGroupCard({
  group,
  form,
  store,
  calcStore,
  hasError,
  disabled,
  loading,
}: CementGroupCardProps) {
  // D-03: alle Gruppen starten zugeklappt. D-15: kein automatisches Aufklappen bei Fehlern --
  // der Startwert haengt deshalb NICHT von hasError ab. D-16: reiner React-State ohne
  // Browser-Persistenz -- der Auf-/Zu-Zustand gilt nur fuer die Sitzung.
  const [expanded, setExpanded] = useState(false);

  return (
    <SectionToggleRow
      variant="card"
      expanded={expanded}
      onToggle={() => setExpanded((open) => !open)}
      title={
        <Group gap="xs" wrap="nowrap">
          <Text component="span" fw={600}>
            {group.label}
          </Text>
          {/* D-14: roter Punkt am Kopf einer Gruppe mit fehlerhaften Feldern -- auch zugeklappt. */}
          {hasError && <TabErrorDot />}
        </Group>
      }
      meta={`${group.types.length} Zementart${group.types.length > 1 ? 'en' : ''}`}
      rightSection={
        // D-02: die Gruppensumme ist IMMER sichtbar, auch bei 0 -- keine Bedingung mehr.
        <FormularDTotal
          form={form}
          store={store}
          calcStore={calcStore}
          serverKey={`summe_gruppe_${group.id}`}
          compute={(values) => calculateGroupTotal(values, group.id)}
        >
          {(total) => (
            // Reiner Text statt eines Badges: die Gruppensumme steht ohne eigene Flaeche im
            // Kartenkopf, 18px/700. Damit entfaellt auch der Umweg ueber textTransform, den
            // der Badge brauchte (sein uppercase machte aus "1.234,50 t" ein "1.234,50 T").
            // D-15: der Vorwert steht darunter, weiterhin rechtsbuendig -- ein schmaler
            // Behaelter statt einer Aenderung an der bestehenden Ausrichtung.
            <Box>
              <Text fz={18} fw={700} style={{ fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>
                {formatTonnage(total)} t
              </Text>
              <PreviousValueForField field={`summe_gruppe_${group.id}`} />
            </Box>
          )}
        </FormularDTotal>
      }
    >
      {/* D-13: Collapse (in SectionToggleRow) haelt die Felder gemountet, auch wenn zugeklappt --
          kein bedingtes Rendern. Eine eigene waagerechte Scroll-Flaeche je Gruppe, nicht eine
          gemeinsame fuer ganz Abschnitt I. */}
      {/* Nur WAAGERECHTES Scrollen. Eine Hoehenbegrenzung (frueher maxHeight: 60vh) erzeugte
          einen zweiten Rollbalken innerhalb des Formulars -- uebersieht man ihn, wirken die
          Zementarten darunter wie nicht vorhanden. Eine Gruppe zeigt jetzt immer alle ihre
          Zeilen; gescrollt wird ausschliesslich die Seite. */}
      <Box style={{ overflowX: 'auto' }}>
        {/* Gezaehlt wird ab der ersten DATENZEILE: die Kopfzeile ist optisch Reihe 1 und
            bleibt ungefaerbt, damit faellt Weiss auf die ungeraden Datenzeilen. */}
        <Table
          className={matrixClasses.table}
          striped="odd"
          stripedColor="var(--mantine-color-white)"
          styles={MATRIX_TABLE_STYLES}
        >
          <Table.Thead>
            <Table.Tr>
              {/* Die Eckzelle bleibt beim waagerechten Scrollen stehen und braucht dafuer eine
                  deckende Farbe -- sie fuehrt den Ton der Karte, auf der sie liegt, und wirkt
                  damit ungefaerbt wie der Rest der Kopfzeile. */}
              <Table.Th style={{ ...MATRIX_LABEL_TH, ...MATRIX_STICKY_LABEL_TH, backgroundColor: 'var(--row-bg)' }}>
                Zementart
              </Table.Th>
              {/* D-01: die neun Festigkeitsklassen stehen genau einmal hier -- nicht mehr als
                  Label ueber jedem einzelnen Feld. */}
              {STRENGTH_CLASSES.map((suffix) => (
                <Table.Th key={suffix} style={MATRIX_VALUE_TH_COMPACT}>
                  {STRENGTH_LABELS[suffix]}
                </Table.Th>
              ))}
              <Table.Th style={MATRIX_TOTAL_TH}>Gesamt</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {group.types.map((type) => (
              <Table.Tr key={type.prefix}>
                {/* D-10: der volle Zementart-Name, nicht die kompakte Kurzform.
                    Der Hintergrund kommt aus --row-bg (matrixTable.module.css) statt aus
                    dem festen Wert in MATRIX_STICKY_LABEL_TD: die fixierte Spalte muss die
                    Farbe IHRER Zeile tragen, sonst zerschneidet sie den Zebrastreifen und
                    die Fokus-Hervorhebung. */}
                <Table.Td
                  style={{ ...MATRIX_STICKY_LABEL_TD, backgroundColor: 'var(--row-bg)', whiteSpace: 'nowrap' }}
                >
                  <Text size="sm">{type.label}</Text>
                </Table.Td>
                {STRENGTH_CLASSES.map((suffix) => {
                  const fieldKey = `${type.prefix}_${suffix}`;
                  return (
                    <Table.Td key={fieldKey}>
                      {loading ? (
                        <Skeleton height={36} radius="sm" />
                      ) : (
                        <ValidatedNumberInput
                          form={form}
                          field={fieldKey}
                          kind="decimal"
                          disabled={disabled}
                          aria-label={`${type.label} ${STRENGTH_LABELS[suffix]}`}
                          suffix=" t"
                          {...DECIMAL_INPUT_PROPS}
                          styles={{ input: { textAlign: 'right', fontVariantNumeric: 'tabular-nums' } }}
                        />
                      )}
                    </Table.Td>
                  );
                })}
                {/* D-11: schlichter Text, kein deaktiviertes Eingabefeld. D-12: '–' bei komplett
                    leerer Zeile ueber formatTonnage.
                    Die Einheit steht nur an einem echten Wert -- '– t' waere sinnlos, und der
                    Figma-Screen zeigt sie ebenfalls nur an gefuellten Zeilen (dort '0 t', was
                    hier wegen D-12 '–' bleibt). */}
                <Table.Td style={{ textAlign: 'right' }}>
                  <FormularDTotal
                    form={form}
                    store={store}
                    calcStore={calcStore}
                    serverKey={`summe_${type.prefix}`}
                    compute={(values) => calculateTypeTotal(values, type.prefix)}
                  >
                    {(total) => (
                      <>
                        <Text size="sm">
                          {formatTonnage(total)}
                          {total != null && ' t'}
                        </Text>
                        <PreviousValueForField field={`summe_${type.prefix}`} />
                      </>
                    )}
                  </FormularDTotal>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Box>
    </SectionToggleRow>
  );
},
(prev, next) =>
  prev.errorSig === next.errorSig &&
  prev.hasError === next.hasError &&
  prev.disabled === next.disabled &&
  prev.loading === next.loading &&
  prev.group.id === next.group.id &&
  prev.formKey === next.formKey);

export function FormularDSectionI({ form, store, calcStore, disabled, loading }: FormularDSectionIProps) {
  // D-14: die bestehende, getestete Funktion aus tabErrors.ts -- keine neue Aggregationslogik.
  const groupsWithErrors = tabsWithErrors(form.errors, CEM_GROUP_FIELD_MAP);
  // D-04: einmal je Render gelesen, dann von cemGroupErrorSig je Gruppe konsumiert.
  const errors = form.errors as Record<string, unknown>;

  return (
    <Stack gap="sm">
      {CEMENT_GROUPS.map((group) => (
        <CementGroupCard
          key={group.id}
          group={group}
          form={form}
          store={store}
          calcStore={calcStore}
          errorSig={cemGroupErrorSig(errors, group.id)}
          formKey={form.key(CEM_GROUP_FIELD_MAP[group.id][0])}
          hasError={groupsWithErrors.has(group.id)}
          disabled={disabled}
          loading={loading}
        />
      ))}
    </Stack>
  );
}
